import { endOfMonth, startOfMonth } from "date-fns";
import { useEffect, useState } from "react";
import { useFormContext } from "react-hook-form";
import { useTranslation } from "react-migration/lib";
import { formatShortMonthAndYear } from "src/js/util/dates";

import {
  Button,
  DatePicker,
  DatePickerInput,
  DropdownMenu,
  DropdownMenuSize,
  DropdownMenuTriggerAlignment,
  Theme,
} from "@landtechnologies/components";

import { LetterBatchesFilterFormData, defaultDateRange } from "./LetterBatchesFilterSchema";

export const DateFilter = () => {
  const { t } = useTranslation();
  const defaultLabel = t("sites.letter_batches.filters.all");
  const { register, setValue, watch } = useFormContext<LetterBatchesFilterFormData>();

  const [startDate, setStartDate] = useState<Date>(defaultDateRange.startDate);
  const [endDate, setEndDate] = useState<Date>(defaultDateRange.endDate);
  const [label, setLabel] = useState<string>(defaultLabel);

  const isDateEqualToDefault = (
    date?: Date,
    datePropertyName: keyof typeof defaultDateRange = "startDate"
  ) =>
    date?.getFullYear() === defaultDateRange[datePropertyName].getFullYear() &&
    date?.getMonth() === defaultDateRange[datePropertyName].getMonth();

  const onApplyFilter = () => {
    if (
      !isDateEqualToDefault(startDate, "startDate") ||
      !isDateEqualToDefault(endDate, "endDate")
    ) {
      setLabel(`${formatShortMonthAndYear(startDate)} - ${formatShortMonthAndYear(endDate)}`);
    }
  };

  const onClearFilter = () => {
    setValue("startDate", defaultDateRange.startDate);
    setValue("endDate", defaultDateRange.endDate);
    setLabel(defaultLabel);
  };

  useEffect(() => {
    watch(({ startDate: newStartDate, endDate: newEndDate }) => {
      if (
        isDateEqualToDefault(newStartDate, "startDate") &&
        isDateEqualToDefault(newEndDate, "endDate")
      ) {
        setLabel(defaultLabel);
      }
      setStartDate(newStartDate ?? startDate);
      setEndDate(newEndDate ?? endDate);
    });
  }, [watch, startDate, endDate]);

  return (
    <div
      className="atlas-flex atlas-items-center atlas-text-center atlas-gap-x-1"
      data-testid="letter-batches-filters-date"
    >
      <span className="atlas-leading-5">{t("sites.letter_batches.filters.date")}</span>

      <DropdownMenu.Provider size={DropdownMenuSize.AUTO}>
        <DropdownMenu.Trigger
          align={DropdownMenuTriggerAlignment.BOTTOM}
          data-testid="date-filter-trigger"
          className="atlas-px-3 atlas-py-1.5 atlas-rounded hover:atlas-bg-background-dark"
        >
          <span className="atlas-font-bold atlas-mr-1">{label}</span>
          <i className="icon-lt-arrow-down-s-fill atlas-text-base" />
        </DropdownMenu.Trigger>
        <DropdownMenu.Content className="atlas-w-[18.5rem] atlas-left-[10%] atlas-p-4">
          <div className="atlas-flex atlas-flex-row atlas-items-center atlas-gap-x-2">
            <span>{t("sites.letter_batches.filters.date_from")}</span>
            <div data-testid="letter-batches-filter-date-start">
              <DatePicker
                wrapperClassName="atlas-w-24 atlas-text-xs atlas-text-neutral-400 atlas-justify-around"
                selected={startDate}
                selectsStart
                startDate={startDate}
                endDate={endDate}
                popperClassName="atlas-z-[1001]"
                dateFormat={"MM/yyyy"}
                showMonthYearPicker={true}
                customInput={<DatePickerInput theme={Theme.Light} />}
                minDate={defaultDateRange.startDate}
                maxDate={endDate}
                {...register("startDate")}
                onChange={(date: Date) =>
                  setValue("startDate", startOfMonth(date), {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  })
                }
              />
            </div>
            <span>{t("sites.letter_batches.filters.date_to")}</span>
            <div data-testid="letter-batches-filter-date-end">
              <DatePicker
                wrapperClassName="atlas-w-24 atlas-text-xs atlas-text-neutral-400 atlas-justify-around"
                selected={endDate}
                selectsEnd
                startDate={startDate}
                endDate={endDate}
                minDate={startDate}
                maxDate={new Date()}
                dateFormat={"MM/yyyy"}
                showMonthYearPicker={true}
                popperClassName="atlas-z-[1001]"
                customInput={<DatePickerInput theme={Theme.Light} />}
                {...register("endDate")}
                onChange={(date: Date) => {
                  setValue("endDate", endOfMonth(date), {
                    shouldDirty: true,
                    shouldTouch: true,
                    shouldValidate: true,
                  });
                }}
              />
            </div>
          </div>
          <div
            className="atlas-flex atlas-flex-row atlas-gap-x-4 atlas-justify-end atlas-mt-3"
            data-testid="letter-batches-filter-actions"
          >
            <Button.Secondary size="small" className="atlas-border-0" onClick={onClearFilter}>
              {t("sites.letter_batches.filters.cancel")}
            </Button.Secondary>
            <Button.Primary size="small" onClick={onApplyFilter}>
              {t("sites.letter_batches.filters.apply")}
            </Button.Primary>
          </div>
        </DropdownMenu.Content>
      </DropdownMenu.Provider>
    </div>
  );
};
